/**
 * Spec 200 Fase 5 #2 — subtask CRUD inside WorkDetail.
 *
 * The mobile shape `Array<{ id, text, done }>` is preserved; this
 * component owns no persistence — every mutation flows through
 * `onChange(nextList)` which the parent commits via the same
 * `broadcastWork` helper used for inline-field edits.
 *
 * UX:
 *   - new subtask: input at the top + Enter (or "Añadir" button);
 *     blur on its own keeps the draft (no save), so a misclick
 *     doesn't lose typing.
 *   - toggle done: checkbox.
 *   - edit text: click the label → InlineEditField (single-line);
 *     empty save deletes the subtask (mobile parity — emptying =
 *     intent to remove without finding the trash icon).
 *   - delete: trash icon at the right.
 *
 * The "drag to reorder" affordance is explicitly out-of-scope per
 * the supervisor brief.
 */
import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';

import { InlineEditField } from './InlineEditField';

import './SubtaskList.css';

export interface Subtask {
  id: string;
  text: string;
  done: boolean;
}

export interface SubtaskListProps {
  subtasks: Subtask[];
  onChange: (next: Subtask[]) => void | Promise<void>;
  /** Forwarded for E2E selectors. */
  testId?: string;
}

function newSubtaskId(): string {
  // Same id space mobile uses (UUID-style 32 hex chars). 16 random
  // bytes is overkill for in-team uniqueness but keeps the wire
  // shape symmetric.
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  let hex = '';
  for (let i = 0; i < bytes.length; i++) hex += bytes[i].toString(16).padStart(2, '0');
  return hex;
}

export function SubtaskList({ subtasks, onChange, testId }: SubtaskListProps) {
  const [draft, setDraft] = useState('');

  async function add(): Promise<void> {
    const text = draft.trim();
    if (!text) return;
    const next: Subtask[] = [...subtasks, { id: newSubtaskId(), text, done: false }];
    setDraft('');
    await onChange(next);
  }

  async function toggle(id: string): Promise<void> {
    const next = subtasks.map((s) => (s.id === id ? { ...s, done: !s.done } : s));
    await onChange(next);
  }

  async function editText(id: string, text: string): Promise<void> {
    const trimmed = text.trim();
    if (!trimmed) {
      // Empty save deletes — same affordance mobile uses.
      await remove(id);
      return;
    }
    const next = subtasks.map((s) => (s.id === id ? { ...s, text: trimmed } : s));
    await onChange(next);
  }

  async function remove(id: string): Promise<void> {
    await onChange(subtasks.filter((s) => s.id !== id));
  }

  return (
    <div className={'subtask-list'} data-testid={testId}>
      <div className="subtask-add-row">
        <input
          type="text"
          className="subtask-add-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void add();
            }
          }}
          placeholder="Nueva subtarea"
          data-testid={testId ? `${testId}-add-input` : undefined}
          aria-label="Nueva subtarea"
        />
        <button
          type="button"
          className="subtask-add-btn"
          onClick={() => void add()}
          disabled={draft.trim().length === 0}
          data-testid={testId ? `${testId}-add-btn` : undefined}
        >
          <Plus size={16} strokeWidth={2} aria-hidden="true" />
          Añadir
        </button>
      </div>

      {subtasks.length === 0 ? (
        <p className="subtask-empty" data-testid={testId ? `${testId}-empty` : undefined}>
          Sin subtareas. Añade la primera arriba.
        </p>
      ) : (
        <ul className="subtask-items">
          {subtasks.map((s) => (
            <li
              key={s.id}
              className={`subtask-item ${s.done ? 'subtask-item--done' : ''}`}
              data-testid={testId ? `${testId}-item-${s.id}` : undefined}
            >
              <input
                type="checkbox"
                className="subtask-checkbox"
                checked={s.done}
                onChange={() => void toggle(s.id)}
                data-testid={testId ? `${testId}-toggle-${s.id}` : undefined}
                aria-label={`Marcar ${s.text} como ${s.done ? 'no hecho' : 'hecho'}`}
              />
              <div className="subtask-text">
                <InlineEditField
                  value={s.text}
                  onSave={(next) => editText(s.id, next)}
                  testId={testId ? `${testId}-text-${s.id}` : undefined}
                  ariaLabel="Editar subtarea"
                />
              </div>
              <button
                type="button"
                className="subtask-delete"
                onClick={() => void remove(s.id)}
                data-testid={testId ? `${testId}-delete-${s.id}` : undefined}
                aria-label="Eliminar subtarea"
              >
                <Trash2 size={16} strokeWidth={2} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
